import React, { useState } from 'react';
import styled from 'styled-components';
import { FiMapPin, FiLoader, FiCheckCircle } from 'react-icons/fi';
import apiClient from '../services/api';
import { toast } from 'react-toastify';

const PanelContainer = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 20px;
`;

const SectionTitle = styled.h3`
  color: #424242;
  margin-bottom: 15px;
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Description = styled.p`
  color: #757575;
  font-size: 0.9rem;
  margin-bottom: 20px;
  line-height: 1.5;
`;

const EnrichButton = styled.button`
  background: linear-gradient(135deg, #4caf50 0%, #66bb6a 100%);
  color: white;
  border: none;
  padding: 12px 30px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
  }
  
  &:disabled {
    background: #bdbdbd;
    cursor: not-allowed;
  }
`;

const ResultBox = styled.div`
  margin-top: 20px;
  padding: 20px;
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border: 2px solid #4caf50;
  border-radius: 10px;
`;

const ResultTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #2e7d32;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ResultStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
`;

const StatCard = styled.div`
  background: #fff;
  padding: 15px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: #2e7d32;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: #616161;
  font-weight: 600;
`;

const EnrichmentPanel = ({ jobId, onEnriched }) => {
  const [enriching, setEnriching] = useState(false);
  const [result, setResult] = useState(null);

  const handleEnrich = async () => {
    setEnriching(true);
    
    try {
      console.log('🌍 Starting postal code enrichment...');
      
      const response = await apiClient.post(`/api/v1/enrich-postal/${jobId}`);
      
      console.log('✅ Enrichment completed:', response.data);
      setResult(response.data);
      
      if (onEnriched) {
        onEnriched(response.data);
      }
      
      toast.success(`✅ Enriched ${response.data.enriched_count} postal codes!`);
    } catch (error) {
      console.error('❌ Enrichment error:', error);
      toast.error('Failed to enrich postal codes: ' + (error.response?.data?.detail || error.message));
    } finally {
      setEnriching(false);
    }
  };

  return (
    <PanelContainer>
      <SectionTitle>
        <FiMapPin /> Auto-Fill Missing Postal Codes
      </SectionTitle>
      
      <Description>
        Automatically lookup and fill missing postal codes using the GeoNames worldwide database.
        The system will match based on City and Country information.
      </Description>

      <EnrichButton onClick={handleEnrich} disabled={enriching}>
        {enriching ? (
          <>
            <FiLoader style={{ animation: 'spin 1s linear infinite' }} />
            Enriching Data...
          </>
        ) : (
          <>
            <FiMapPin />
            Auto-Fill Postal Codes
          </>
        )}
      </EnrichButton>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {result && (
        <ResultBox>
          <ResultTitle>
            <FiCheckCircle />
            Enrichment Results
          </ResultTitle>
          <ResultStats>
            <StatCard>
              <StatValue>{result.enriched_count}</StatValue>
              <StatLabel>Enriched</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{result.already_complete_count}</StatValue>
              <StatLabel>Already Complete</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{result.failed_count}</StatValue>
              <StatLabel>Could Not Find</StatLabel>
            </StatCard>
          </ResultStats>
        </ResultBox>
      )}
    </PanelContainer>
  );
};

export default EnrichmentPanel;